/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import i18n, { Resource, ResourceLanguage } from 'i18next';
import merge from 'lodash/merge';
import { initReactI18next } from 'react-i18next';
import common from './locales/en-us/common.json';

export const i18nSharedInstance: typeof i18n = i18n.createInstance();

i18nSharedInstance
  .use(initReactI18next) // passes i18n down to react-i18next.
  .init(
    merge({
      defaultNS: `translations`,
      // React already safe from xss.
      interpolation: {
        escapeValue: false,
      },
      lng: `en`,
      resources: {
        [`en`]: {
          common,
        },
      },
    }),
  );

/**
 * App I18nResource.
 * @interface I18nAppResource
 */
export interface I18nAppResource extends ResourceLanguage {
  translations: object;
}

/**
 * App I18nResources.
 * @interface I18nAppResources
 */
export interface I18nAppResources {
  [`en`]: I18nAppResource;
}

/**
 * The default i18n resource language.
 * @export
 * @interface I18nResourceLanguage
 * @extends {ResourceLanguage}
 */
export interface I18nResourceLanguage extends ResourceLanguage {
  common: typeof common;
}

/**
 * The default interface for I18nResources.
 * @export
 * @interface I18nResource
 * @extends {ResourceLanguage}
 */
export interface I18nResource extends Resource {
  [`en`]: I18nResourceLanguage;
}

declare module 'react-i18next' {
  /**
   * Custom type options.
   */
  interface CustomTypeOptions {
    defaultNS: 'common';
    resources: I18nResource;
  }
}

/**
 * Add new translations to existing bundle.
 * @export
 * @template R
 * @param {R} resources The translation resource strings to add.
 * @param {string} lang The language.
 * @param {string} namespace The namespace.
 */
export function addTranslations<R extends object>(resources: R, lang = `en`, namespace = `translations`): void {
  i18nSharedInstance.addResourceBundle(lang, namespace, resources);
}
